import React from 'react';
import {useTranslation} from 'react-i18next';

import {Icon, Stack} from '@edna/components/primitives';
import {TSpacingSize} from '@edna/components/types';
import CalendarIcon from '@edna/icons/calendar.svg';
import EditIcon from '@edna/icons/edit.svg';
import TimeIcon from '@edna/icons/time.svg';

import {StatusIcon} from 'src/components';
import {Text} from 'src/components/primitives';
import {CHANNEL_LABEL, EChannelType, TSubject, channelIconHash} from 'src/constants';
import {EyeIcon, LinkIcon, ReturnIcon, TrashIcon, WarningIcon} from 'src/icons';
import {parseISO} from 'src/utils';

import {
  showAvailableTariffHistoryModal,
  showCancelConnectedTariffChangesModal,
  showCancelTariffChangesModal,
  showChangeDateForConnectedTariff,
  showChangeDateForPlannedTariff,
  showConnectedTariffHistoryModal,
  showConnectedTariffSettingsModal,
  showDeleteTariffModal,
  showEditConnectedTariffModal,
  showEditTariffModal,
  showHideTariffModal,
  showLinkedSubjectsModal,
  showTariffSettingsModal,
} from '../TariffModals';
import {TariffPricing} from '../TariffPricing';
import {useRevealAvailableTariffMutation} from '../api';
import {ETariffViewType, TAvailableTariffResponse, TConnectedTariffResponse} from '../definitions';
import * as S from './style';

const Detail = React.memo<{label: string; rightIndent?: TSpacingSize; children: React.ReactNode}>(
  ({label, rightIndent, children}) => {
    return (
      <S.Detail rightIndent={rightIndent}>
        <Text secondary size="S">
          {label}
        </Text>
        {children}
      </S.Detail>
    );
  },
);

Detail.displayName = 'Detail';

export const TariffCardWrapper = React.memo<{
  channelType: EChannelType;
  children: React.ReactNode;
  innerRef?: React.RefCallback<HTMLDivElement>;
}>(({channelType, innerRef, children}) => {
  return (
    <S.Card ref={innerRef}>
      <S.ChannelLabel>
        <Icon size="20px" as={channelIconHash[channelType]} />
        {CHANNEL_LABEL[channelType]}
      </S.ChannelLabel>
      <Stack size="3" isFullWidth>
        {children}
      </Stack>
    </S.Card>
  );
});

TariffCardWrapper.displayName = 'TariffCardWrapper';

enum EActionType {
  SHOW_SETTINGS = 'SHOW_SETTINGS',
  SHOW_CHANGES = 'SHOW_CHANGES',
  CHANGE_TARIFF = 'CHANGE_TARIFF',
  CANCEL_CHANGES = 'CANCEL_CHANGES',
  CHANGE_DATE = 'CHANGE_DATE',
  DELETE_TARIFF = 'DELETE_TARIFF',
  REVEAL_TARIFF = 'REVEAL_TARIFF',
}

const TARIFF_ACTIONS = Object.values(EActionType);

const actionsHash: Record<ETariffViewType, EActionType[]> = {
  [ETariffViewType.CLIENT]: [],
  [ETariffViewType.BILLING_DEPARTMENT_ACCOUNT]: TARIFF_ACTIONS,
  [ETariffViewType.SUPPORT]: TARIFF_ACTIONS.filter((value) => value !== EActionType.CHANGE_DATE),
  [ETariffViewType.MANAGER]: [
    EActionType.SHOW_SETTINGS,
    EActionType.SHOW_CHANGES,
    EActionType.DELETE_TARIFF,
  ],
};

type TAction = {icon: TSvgComponentType; label: string; handler: TEmptyFunction};

type TProps = {
  availableTariff: TAvailableTariffResponse;
  subject?: TSubject;
  linkedSubjects?: number[];
  connectedTariff?: TConnectedTariffResponse;
  innerRef?: React.RefCallback<HTMLDivElement>;
  onTariffLinkClick?: TEmptyFunction;
  viewType?: keyof typeof ETariffViewType;
};

export const TariffCard = React.memo<TProps>(
  ({
    connectedTariff,
    availableTariff,
    linkedSubjects = [],
    subject,
    innerRef,
    onTariffLinkClick,
    viewType = ETariffViewType.CLIENT,
  }) => {
    const {t} = useTranslation();
    const isVisibleTariff = availableTariff.active.visible !== false;

    const [revealTariff] = useRevealAvailableTariffMutation();

    const handleDeleteTariff = React.useCallback(
      () =>
        linkedSubjects.length
          ? showHideTariffModal(availableTariff.active, linkedSubjects)
          : showDeleteTariffModal(availableTariff.active),
      [availableTariff, linkedSubjects],
    );

    const handleRevealTariff = React.useCallback(() => {
      revealTariff(availableTariff.active.id);
    }, [availableTariff.active]);

    const handleShowTariffHistory = React.useCallback(() => {
      if (connectedTariff) {
        return showConnectedTariffHistoryModal(connectedTariff.active);
      }

      return showAvailableTariffHistoryModal(availableTariff.active);
    }, [availableTariff.active, connectedTariff]);

    const actions = React.useMemo<Partial<Record<EActionType, TAction | null>>>(() => {
      const tariff = availableTariff.active;

      if (connectedTariff?.planned) {
        const plannedConnectedTariff = connectedTariff.planned;

        return {
          [EActionType.SHOW_SETTINGS]: {
            icon: EyeIcon,
            label: t('Tariffs:actions.showSettings'),
            handler: () => showTariffSettingsModal(tariff),
          },
          [EActionType.SHOW_CHANGES]: {
            icon: EyeIcon,
            label: t('Tariffs:actions.showChanges'),
            handler: () =>
              showConnectedTariffSettingsModal({
                tariffId: plannedConnectedTariff.selectedTariffId,
                activeFrom: plannedConnectedTariff.activeFrom,
              }),
          },
          [EActionType.CANCEL_CHANGES]: {
            icon: WarningIcon,
            label: t('Tariffs:actions.cancelChanges'),
            handler: () =>
              showCancelConnectedTariffChangesModal({
                id: plannedConnectedTariff.id,
                version: plannedConnectedTariff.version,
                name: tariff.name,
              }),
          },
          [EActionType.CHANGE_DATE]: {
            icon: CalendarIcon,
            label: t('Tariffs:actions.changeDate'),
            handler: () => showChangeDateForConnectedTariff(plannedConnectedTariff, tariff.name),
          },
        };
      }

      const plannedTariff = availableTariff.planned;

      if (connectedTariff) {
        return {
          [EActionType.SHOW_SETTINGS]: {
            icon: EyeIcon,
            label: t('Tariffs:actions.showSettings'),
            handler: () => showTariffSettingsModal(tariff),
          },
          [EActionType.SHOW_CHANGES]: plannedTariff
            ? {
                icon: EyeIcon,
                label: t('Tariffs:actions.showChanges'),
                handler: () => showTariffSettingsModal(plannedTariff),
              }
            : null,
          [EActionType.CHANGE_TARIFF]:
            tariff.channelType === EChannelType.SMS
              ? null
              : {
                  icon: EditIcon,
                  label: t('Tariffs:actions.changeTariff'),
                  handler: () => showEditConnectedTariffModal(tariff, connectedTariff.active),
                },
        };
      }

      let allActions: Partial<Record<EActionType, TAction | null>> = {
        [EActionType.SHOW_SETTINGS]: {
          icon: EyeIcon,
          label: t('Tariffs:actions.showSettings'),
          handler: () => showTariffSettingsModal(tariff),
        },
      };

      if (!plannedTariff) {
        allActions[EActionType.CHANGE_TARIFF] = {
          icon: EditIcon,
          label: t('Tariffs:actions.changeTariff'),
          handler: () => showEditTariffModal(tariff),
        };
      }

      if (plannedTariff) {
        allActions = {
          ...allActions,
          [EActionType.SHOW_CHANGES]: {
            icon: EyeIcon,
            label: t('Tariffs:actions.showChanges'),
            handler: () => showTariffSettingsModal(plannedTariff),
          },
          [EActionType.CANCEL_CHANGES]: {
            icon: WarningIcon,
            label: t('Tariffs:actions.cancelChanges'),
            handler: () => showCancelTariffChangesModal(plannedTariff),
          },
          [EActionType.CHANGE_DATE]: {
            icon: CalendarIcon,
            label: t('Tariffs:actions.changeDate'),
            handler: () => showChangeDateForPlannedTariff(plannedTariff),
          },
        };
      }

      if (isVisibleTariff) {
        allActions[EActionType.DELETE_TARIFF] = {
          icon: TrashIcon,
          label: t('Tariffs:actions.deleteTariff'),
          handler: handleDeleteTariff,
        };
      }

      if (!isVisibleTariff) {
        allActions[EActionType.REVEAL_TARIFF] = {
          icon: ReturnIcon,
          label: t('Tariffs:actions.revealTariff'),
          handler: handleRevealTariff,
        };
      }

      return allActions;
    }, [handleDeleteTariff, t, viewType, availableTariff, connectedTariff]);

    const actionsOptions = React.useMemo(
      () =>
        Object.values(EActionType)
          .filter((actionType) => actionsHash[viewType].includes(actionType) && actions[actionType])
          .map((actionType) => {
            const action = actions[actionType] as TAction;

            return {
              content: (
                <>
                  <S.LeftIcon as={action.icon} /> {action.label}
                </>
              ),
              handler: action.handler,
            };
          }),
      [actions],
    );

    return (
      <TariffCardWrapper channelType={availableTariff.active.channelType} innerRef={innerRef}>
        <Detail label={t('Tariffs:tariffCard.tariff')} rightIndent="10">
          {availableTariff.active.name}
          {!isVisibleTariff && (
            <S.Detail stackSize="1" secondary size="S">
              <StatusIcon name="cancel" />
              {t('Tariffs:tariffCard.invisible')}
            </S.Detail>
          )}
          {(!!availableTariff.planned || !!connectedTariff?.planned) && (
            <S.Detail stackSize="1" secondary size="S">
              <StatusIcon name="warning" />
              {t(
                connectedTariff?.planned && viewType !== ETariffViewType.CLIENT
                  ? 'Tariffs:tariffCard.willChangeTariff'
                  : 'Tariffs:tariffCard.willChange',
                {
                  date: parseISO(
                    connectedTariff?.planned?.activeFrom ?? availableTariff.planned?.activeFrom,
                    'DATE',
                  ),
                },
              )}
            </S.Detail>
          )}
          {!!linkedSubjects.length && (
            <S.InlineLinkButton onClick={() => showLinkedSubjectsModal(linkedSubjects)}>
              <LinkIcon />
              {t('Tariffs:tariffCard.linkedSubjects')}
            </S.InlineLinkButton>
          )}
          {!!subject && (
            <S.InlineLinkButton onClick={onTariffLinkClick}>
              <LinkIcon />
              {t('Tariffs:tariffCard.linkedTariff', {name: availableTariff.active.name})}
            </S.InlineLinkButton>
          )}
        </Detail>
        {!!subject && (
          <Detail label={t('Tariffs:tariffCard.channel')}>
            <Icon size="24px" as={channelIconHash[availableTariff.active.channelType]} />
            {subject.name}
          </Detail>
        )}
        <Detail label={t('Tariffs:tariffCard.activeFrom')}>
          {parseISO(
            connectedTariff?.active.activeFrom ?? availableTariff.active.activeFrom,
            'DATE',
          )}
          {viewType === ETariffViewType.SUPPORT && (
            <S.InlineLinkButton color="grey600" weight={500} onClick={handleShowTariffHistory}>
              <Icon as={TimeIcon} size="16px" />
              {t('Tariffs:tariffCard.history')}
            </S.InlineLinkButton>
          )}
        </Detail>
        <TariffPricing tariff={availableTariff.active} subjectId={subject?.id} />
        {!!actionsHash[viewType].length && (
          <S.ActionButton
            dropdownComponent={S.FloatingDropdown}
            options={actionsOptions}
            size="S"
            square
            appearance="secondary"
          >
            <S.MoreHorizIcon />
          </S.ActionButton>
        )}
      </TariffCardWrapper>
    );
  },
);

TariffCard.displayName = 'TariffCard';
